export interface AdaptiveSection {
  id: number
  heading: string
  contentBefore: string
  contentAfter: string
}

export const ADAPTIVE_SECTIONS: AdaptiveSection[] = [
  {
    id: 1,
    heading: 'Not All Practice Is Created Equal',
    contentBefore:
      "Open any traditional question bank and you'll find the same experience for every user. The same questions in the same order, regardless of whether you've been in your field for fifteen years or fifteen days. It's the educational equivalent of handing every patient the same prescription.",
    contentAfter:
      "Adaptive learning flips this model. It uses performance data from {{every question you answer}} to build a real-time map of what you know and what you don't. Topics you're strong in get deprioritized. Topics you're struggling with get surfaced more frequently at calibrated difficulty levels. **PrepSolution is built on this model.**",
  },
  {
    id: 2,
    heading: '225 Studies. One Conclusion.',
    contentBefore:
      "In 2014, Scott Freeman's team at the University of Washington published the largest meta-analysis of education methods ever conducted. They examined {{225 separate studies}} comparing traditional lecturing to active learning across STEM disciplines.",
    contentAfter:
      'Students in active learning environments scored 6% higher on exams. More importantly, students in traditional courses were {{1.5 times more likely to fail}} outright. A separate 2022 meta-analysis replicated nearly identical findings in the humanities and social sciences. {{Active learning works regardless of field.}}',
  },
  {
    id: 3,
    heading: 'Your Brain Remembers What It Retrieves, Not What It Sees',
    contentBefore:
      "Students who study material four times remember less than students who study it once and test themselves three times. Roediger and Karpicke's landmark 2006 experiments showed that practice tests led to {{retained 50% more material}} after one week compared to re-reading.",
    contentAfter:
      'The mechanism is called "retrieval practice." When you force your brain to {{pull information out of storage}} rather than re-exposing it to the same material, you create fundamentally stronger memory traces. It\'s the difference between recognizing a face in a photo and describing it from memory. That difficulty is precisely what makes the memory stick.',
  },
  {
    id: 4,
    heading: 'What Happens Between Every Question',
    contentBefore:
      "Most people think a question bank just shuffles cards from a deck. But that ignores the most valuable data available: {{how you're performing right now}}. PrepSolution's adaptive algorithm runs a continuous cycle after every single response.",
    contentAfter:
      'It records your correctness, response time, domain, and difficulty level — then recalibrates. Strong in cardiovascular? The engine {{reduces its weight in your next session}}. Struggling in pharmacology? Priority increases and difficulty drops temporarily to build foundations. By 500 questions, it knows your profile better than you do.',
  },
  {
    id: 5,
    heading: "You Study What's Comfortable. We Study What's Necessary.",
    contentBefore:
      "When left to their own devices, students consistently spend more time on topics they already understand. Yang et al.'s 2022 meta-analysis found that people's {{self-assessed mastery is barely more reliable than guessing}}. Reading is recognition, not recall — completely different cognitive processes.",
    contentAfter:
      "PrepSolution's engine doesn't let you hide in your comfort zone. It {{systematically exposes your weak spots}} and keeps routing questions to those domains until the amber shrinks and the green expands. You don't decide what to study next. The data decides for you, and the data doesn't have an ego.",
  },
  {
    id: 6,
    heading: 'Your Memory Has a Built-In Self-Destruct Timer',
    contentBefore:
      'In 1885, Ebbinghaus mapped exactly how quickly the brain discards new information. Without review, people forget roughly {{50% of new material within one hour}} and approximately {{70% within 24 hours}}. Modern researchers have confirmed this data.',
    contentAfter:
      "But every time you revisit material, the forgetting slows dramatically. Cepeda et al.'s meta-analysis of 254 studies confirmed that {{spaced practice produces 10 to 30% better long-term retention}} than cramming. PrepSolution's spaced repetition engine determines exactly when each concept should reappear — at {{precisely the moment your memory needs it most}}.",
  },
  {
    id: 7,
    heading: "The Cognitive Science Doesn't Care What Exam You're Taking",
    contentBefore:
      "The testing effect doesn't discriminate by industry. Your brain processes information the same way whether the content is about {{project scheduling for the PMP}}, {{clinical judgment for the NCLEX}}, {{HR competencies for the SHRM-CP}}, or {{reading comprehension for the SAT}}.",
    contentAfter:
      "That's why PrepSolution applies the same adaptive engine across every exam. The algorithm only cares about three things: did you get it right, how long did it take, and what should come next. The content is exam-specific. The methodology is universal.",
  },
  {
    id: 8,
    heading: "The Evidence Is Clear. How You Practice Is How You'll Perform.",
    contentBefore:
      'PrepSolution is built on {{decades of peer-reviewed cognitive science}} replicated across thousands of participants and hundreds of studies. Active retrieval outperforms passive review. Spaced practice outperforms cramming. Adaptive targeting outperforms self-directed guesswork.',
    contentAfter:
      'When you practice thousands of questions under adaptive, exam-like conditions, the exam stops being an unknown threat. Your accuracy stabilizes. Your speed improves. Your confidence becomes {{grounded in data, not hope}}. The preparation earns the result before you ever walk into the testing center.',
  },
]

function getSection(id: number): AdaptiveSection {
  const section = ADAPTIVE_SECTIONS.find((s) => s.id === id)
  if (!section) throw new Error(`Unknown adaptive section id: ${id}`)
  return section
}

export interface AdaptiveIllustratedSection {
  section: AdaptiveSection
  image: string
  imageAlt: string
}

export const ADAPTIVE_TABS: AdaptiveIllustratedSection[] = [
  {
    section: getSection(6),
    image: '/images/adaptive-learning/tab-forgetting.png',
    imageAlt:
      'Flat illustration of the forgetting curve dropping steeply while spaced reviews lift retention back up',
  },
  {
    section: getSection(3),
    image: '/images/adaptive-learning/tab-passive.png',
    imageAlt:
      'Flat illustration contrasting passive re-reading with active retrieval practice locking knowledge in',
  },
  {
    section: getSection(5),
    image: '/images/adaptive-learning/tab-comfort-zone.png',
    imageAlt:
      'Flat illustration of a study radar exposing neglected weak domains outside the comfort zone',
  },
]

export interface AdaptiveEngineCard {
  section: AdaptiveSection
  art: 'map' | 'cycle' | 'universal'
  link: { href: string; label: string }
}

export const ADAPTIVE_ENGINE_CARDS: AdaptiveEngineCard[] = [
  {
    section: getSection(1),
    art: 'map',
    link: { href: '#evidence', label: 'See the evidence' },
  },
  {
    section: getSection(4),
    art: 'cycle',
    link: {
      href: '/free-study-materials',
      label: 'Try 10 real questions free',
    },
  },
  {
    section: getSection(7),
    art: 'universal',
    link: { href: '/exams', label: 'Find your exam' },
  },
]

export interface AdaptiveEvidenceContent {
  research: AdaptiveSection
  conclusion: AdaptiveSection
  image: string
  imageAlt: string
}

export const ADAPTIVE_EVIDENCE: AdaptiveEvidenceContent = {
  research: getSection(2),
  conclusion: getSection(8),
  image: '/images/adaptive-learning/evidence.png',
  imageAlt:
    'Flat illustration of stacked peer-reviewed studies feeding into one clear performance conclusion',
}

export interface AdaptiveStat {
  value: number
  suffix: string
  label: string
}

export const STAT_BAR_ITEMS: AdaptiveStat[] = [
  { value: 225, suffix: '', label: 'Studies analyzed' },
  { value: 6, suffix: '%', label: 'Higher exam scores' },
  { value: 1.5, suffix: 'x', label: 'Lower failure rate' },
  { value: 70, suffix: '%', label: 'Forgotten within 24 hours' },
]

export interface AdaptiveFaq {
  question: string
  answer: string
}

export const ADAPTIVE_FAQS: AdaptiveFaq[] = [
  {
    question: 'What is adaptive learning?',
    answer:
      "Adaptive learning uses performance data from every question you answer to build a real-time map of what you know and what you don't. Topics you're strong in get deprioritized, while topics you're struggling with get surfaced more frequently at calibrated difficulty levels.",
  },
  {
    question: 'How is this different from a normal question bank?',
    answer:
      "A traditional question bank serves the same questions in the same order to every user. PrepSolution's adaptive algorithm runs a continuous cycle after every single response — recording correctness, response time, domain, and difficulty — then recalibrates, so your weak domains get priority instead of staying hidden in your comfort zone.",
  },
  {
    question: 'How many questions before it adapts to me?',
    answer:
      'The engine recalibrates after every single response, so adaptation begins with your very first session. The profile keeps sharpening the more you practice — by 500 questions, it knows your profile better than you do.',
  },
  {
    question: 'Does adaptive learning work for my exam?',
    answer:
      "Yes. The testing effect doesn't discriminate by industry — your brain processes information the same way whether the content is project scheduling for the PMP, clinical judgment for the NCLEX, or HR competencies for the SHRM-CP. The content is exam-specific; the methodology is universal.",
  },
  {
    question: 'Is this actually backed by research?',
    answer:
      'PrepSolution is built on decades of peer-reviewed cognitive science, including a meta-analysis of 225 studies showing active learning raises exam scores and lowers failure rates, and research showing spaced practice produces 10 to 30% better long-term retention than cramming. The full reference list is published on this page.',
  },
]

export const ADAPTIVE_REFERENCES = [
  'Freeman, S., Eddy, S. L., McDonough, M., Smith, M. K., Okoroafor, N., Jordt, H., & Wenderoth, M. P. (2014). Active learning increases student performance in science, engineering, and mathematics. Proceedings of the National Academy of Sciences, 111(23), 8410–8415.',
  'Ménard, L., et al. (2022). Effect of active learning versus traditional lecturing on the learning achievement of college students in humanities and social sciences. Higher Education.',
  'Roediger, H. L., & Karpicke, J. D. (2006). Test-enhanced learning. Psychological Science, 17(3), 249–255.',
  'Rowland, C. A. (2014). The effect of testing versus restudy on retention. Psychological Bulletin, 140(6), 1432–1463.',
  'Adesope, O. O., Trevisan, D. A., & Sundararajan, N. (2017). Rethinking the use of tests. Review of Educational Research, 87(3), 659–701.',
  'Ebbinghaus, H. (1885/1913). Memory. Teachers College, Columbia University.',
  "Murre, J. M. J., & Dros, J. (2015). Replication and analysis of Ebbinghaus' forgetting curve. PLOS ONE, 10(7), e0120644.",
  'Cepeda, N. J., Pashler, H., Vul, E., Wixted, J. T., & Rohrer, D. (2006). Distributed practice in verbal recall tasks. Psychological Bulletin, 132(3), 354–380.',
  'Yang, C., et al. (2022). Metacomprehension accuracy. Educational Psychology Review.',
  'Personalized adaptive learning in higher education (2024). Heliyon scoping review, 69 studies (2012–2024). PMC/Heliyon.',
]
